import { GoogleOAuthProvider } from "@react-oauth/google";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import React from "react";
import ReactDOM from "react-dom/client";
import { createBrowserRouter, RouterProvider } from "react-router-dom";

import { setRouterInstance } from "@/api/rest/core/apiClient";
import { AccountPage } from "@/app/account/AccountPage";
import AnalyticsPage from "@/app/analytics/AnalyticsPage";
import BuildingPage from "@/app/building/BuildingPage";
import BuildingSettingsPage from "@/app/building/BuildingSettingsPage";
import BackofficePage from "@/app/backoffice/BackofficePage";
import HomePage from "@/app/household/HomePage";
import QuestionListPage from "@/app/household/question/QuestionListPage";
import WarrantyPage from "@/app/household/warranty/WarrantyPage";
import Login from "@/app/login/LoginPage";
import { AuthType } from "@/app/login/LoginType";
import QuestionPage from "@/app/question/QuestionPage";
import CasePage from "@/app/warranty/CasePage.tsx";
import { PostHogWrapper } from "@/components/analytics/PostHogProvider";
import { ErrorBoundary, RouterErrorBoundary } from "@/components/ErrorBoundary";
import { WebviewInitializer } from "@/utils/webview/WebViewInitializer";

import { CaseType } from "./api/rest/models/warranty.ts";
import Household from "./app/household/HouseholdPage";
import LoggedInPage from "./app/household/LoggedInPage";
import ProductEdit from "./app/household/product/ProductEditPage";
import PropertyManagerCasePage from "./app/household/property-manager/PropertyManagerCasePage.tsx";
import UnsubscribePage from "./components/UnsubscribePage";
import "./i18n";
import "./index.css";
import { debug } from "./utils/debug/logger.ts";
import { shouldFilterError } from "./utils/sentry.ts";

Sentry.init({
  dsn: import.meta.env.MODE === "production" ? (import.meta.env.VITE_SENTRY_DSN as string) : undefined,
  enabled: import.meta.env.MODE === "production",
  integrations: [
    Sentry.browserTracingIntegration(),
    // Sentry.replayIntegration({
    //     maskAllText: false,
    //     blockAllMedia: false,
    // }),
  ],
  beforeSend: (event: Sentry.ErrorEvent) => {
    if (shouldFilterError(event.message)) {
      return null;
    }

    const shouldFilterException = event.exception?.values?.some((exception) =>
      shouldFilterError({
        name: exception.type,
        message: exception.value,
      }),
    );

    return shouldFilterException ? null : event;
  },

  // Tracing
  tracePropagationTargets: [import.meta.env.VITE_API_URL as string],
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Session Replay
  replaysSessionSampleRate: 1.0,
  replaysOnErrorSampleRate: 1.0,
});

debug.log("main", "DoBu frontend bootstrap completed.");

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 min — data stays fresh, no refetch on navigation
      gcTime: 10 * 60 * 1000, // 10 min — keep unused cache entries longer
      refetchOnWindowFocus: false, // don't refetch when switching tabs during demo
    },
  },
});

// Keep the login route in the entry module. Production appends a deployment
// query string to the entry script, so a lazy login chunk importing that entry
// without the query would execute a second React/router module instance.
const renderLoginPage = (authType: AuthType) => <Login authType={authType} />;

const router = createBrowserRouter([
  { path: "/", element: renderLoginPage(AuthType.LOGIN) },
  {
    path: "/login",
    element: renderLoginPage(AuthType.LOGIN),
  },
  {
    path: "/login/about",
    element: renderLoginPage(AuthType.ABOUT),
  },
  {
    path: "/reset-password",
    element: renderLoginPage(AuthType.RESET_PASSWORD),
  },
  {
    path: "/forgot-password",
    element: renderLoginPage(AuthType.FORGOT_PASSWORD),
  },
  {
    path: "/change-email",
    element: renderLoginPage(AuthType.CHANGE_EMAIL),
  },
  {
    path: "/household/",
    element: <LoggedInPage />,
    children: [
      {
        path: "",
        element: <HomePage />,
      },
      {
        path: ":householdId",
        element: <Household />,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: ":householdId/product/:productId",
        element: <ProductEdit mode={"update"} />,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: ":householdId/product/new",
        element: <ProductEdit mode={"create"} />,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: ":householdId/warranty",
        element: <WarrantyPage />,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: ":householdId/property-manager-case",
        element: <PropertyManagerCasePage />,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: ":householdId/questions",
        element: <QuestionListPage />,
        errorElement: <RouterErrorBoundary />,
      },
    ],
  },
  {
    path: "/warranty/",
    element: <LoggedInPage />,
    children: [
      {
        path: ":caseId",
        element: <CasePage type={CaseType.WARRANTY} />,
        errorElement: <RouterErrorBoundary />,
      },
    ],
  },
  {
    path: "/property-manager-case/",
    element: <LoggedInPage />,
    children: [
      {
        path: ":caseId",
        element: <CasePage type={CaseType.PROPERTY_MANAGER_CASE} />,
        errorElement: <RouterErrorBoundary />,
      },
    ],
  },
  {
    path: "/question/",
    element: <LoggedInPage />,
    children: [
      {
        path: ":questionId",
        element: <QuestionPage />,
        errorElement: <RouterErrorBoundary />,
      },
    ],
  },
  {
    path: "/building/",
    element: <LoggedInPage />,
    children: [
      {
        path: ":buildingId",
        element: <BuildingPage />,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: ":buildingId/settings",
        element: <BuildingSettingsPage />,
        errorElement: <RouterErrorBoundary />,
      },
    ],
  },
  {
    path: "/account/",
    element: <LoggedInPage />,
    children: [
      {
        path: "",
        element: <AccountPage />,
        errorElement: <RouterErrorBoundary />,
      },
    ],
  },
  {
    path: "/analytics/",
    element: <LoggedInPage />,
    children: [
      {
        path: "",
        element: <AnalyticsPage />,
        errorElement: <RouterErrorBoundary />,
      },
    ],
  },
  {
    path: "/backoffice/",
    element: <LoggedInPage />,
    children: [
      {
        path: "",
        element: <BackofficePage />,
        errorElement: <RouterErrorBoundary />,
      },
    ],
  },
  {
    path: "/unsubscribe/:token",
    element: <UnsubscribePage />,
    errorElement: <RouterErrorBoundary />,
  },
  {
    path: "/not-found",
    element: <LoggedInPage />,
    children: [
      {
        path: "",
        element: <div className="text-xl">Not found</div>,
      },
    ],
  },
]);

setRouterInstance(router);

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <PostHogWrapper>
        <GoogleOAuthProvider clientId="338357809874-90emau8kl6hhg24p1aphpqo7v25n7po1.apps.googleusercontent.com">
          <QueryClientProvider client={queryClient}>
            <RouterProvider router={router} />
            <WebviewInitializer />
          </QueryClientProvider>
        </GoogleOAuthProvider>
      </PostHogWrapper>
    </ErrorBoundary>
  </React.StrictMode>,
);
